import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MagnifyingGlassMinusIcon,
    MagnifyingGlassPlusIcon,
    PrinterIcon
} from '@heroicons/react/24/outline';
import React from 'react';

const PILL_CLASS = 'flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0';
const PILL_BUTTON_CLASS =
    'p-1.5 rounded-md text-gray-600 hover:bg-white hover:shadow-sm transition-colors';
const STEP_BUTTON_CLASS = (enabled: boolean) =>
    `p-1.5 rounded-md transition-colors ${enabled ? 'text-gray-600 hover:bg-white hover:shadow-sm' : 'text-gray-300 cursor-not-allowed'}`;

interface PageNavData {
    numPages: number | null;
    currentPage: number;
    canGoPrev: boolean;
    canGoNext: boolean;
    onPrevPage: () => void;
    onNextPage: () => void;
}

function PageNavPill({
    numPages,
    currentPage,
    canGoPrev,
    canGoNext,
    onPrevPage,
    onNextPage
}: PageNavData) {
    return (
        <div className={PILL_CLASS}>
            <button
                className={STEP_BUTTON_CLASS(canGoPrev)}
                onClick={onPrevPage}
                disabled={!canGoPrev}
                title="Previous page"
                aria-label="Previous page"
            >
                <ChevronLeftIcon className="w-4 h-4" />
            </button>
            {/* aria-label is ignored on a roleless span, so the long form is a real node. */}
            <span className="text-sm text-gray-600 whitespace-nowrap px-1 tabular-nums" aria-hidden>
                {numPages ? `${currentPage} / ${numPages}` : '–'}
            </span>
            <span className="sr-only">
                {numPages ? `Page ${currentPage} of ${numPages}` : 'Page count loading'}
            </span>
            <button
                className={STEP_BUTTON_CLASS(canGoNext)}
                onClick={onNextPage}
                disabled={!canGoNext}
                title="Next page"
                aria-label="Next page"
            >
                <ChevronRightIcon className="w-4 h-4" />
            </button>
        </div>
    );
}

interface ZoomData {
    scale: number;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomReset: () => void;
}

function ZoomPill({ scale, onZoomIn, onZoomOut, onZoomReset }: ZoomData) {
    return (
        <div className={PILL_CLASS}>
            <button
                className={PILL_BUTTON_CLASS}
                onClick={onZoomOut}
                title="Zoom out"
                aria-label="Zoom out"
            >
                <MagnifyingGlassMinusIcon className="w-4 h-4" />
            </button>
            {/* The percentage doubles as Reset - it is the only thing a reset would restore. */}
            <button
                className="text-sm text-gray-600 w-11 text-center font-medium tabular-nums rounded-md py-1 cursor-pointer hover:bg-white hover:shadow-sm hover:underline underline-offset-2 transition-colors"
                onClick={onZoomReset}
                title="Reset zoom to 100%"
                aria-label="Reset zoom to 100%"
            >
                {Math.round(scale * 100)}%
            </button>
            <button
                className={PILL_BUTTON_CLASS}
                onClick={onZoomIn}
                title="Zoom in"
                aria-label="Zoom in"
            >
                <MagnifyingGlassPlusIcon className="w-4 h-4" />
            </button>
        </div>
    );
}

interface ToolbarActionButtonProps {
    label: string;
    icon: React.ReactNode;
    disabled: boolean;
    onClick: () => void;
}

// Icon-only at every width: a label that only sometimes fits is a layout that
// only sometimes works. The tooltip and aria-label carry the name instead.
function ToolbarActionButton({ label, icon, disabled, onClick }: ToolbarActionButtonProps) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            className="inline-flex items-center justify-center bg-gray-900 text-white p-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
            {icon}
        </button>
    );
}

interface ToolbarActionsData {
    onPrint: () => void;
    onDownload: () => void;
    canUseActions: boolean;
    isPrinting: boolean;
    isDownloading: boolean;
}

function ToolbarActions({
    onPrint,
    onDownload,
    canUseActions,
    isPrinting,
    isDownloading
}: ToolbarActionsData) {
    return (
        <div className="flex items-center gap-2 ml-1 flex-shrink-0">
            <ToolbarActionButton
                label="Print"
                disabled={!canUseActions || isPrinting}
                onClick={onPrint}
                icon={
                    isPrinting ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                        <PrinterIcon className="w-4 h-4" />
                    )
                }
            />
            <ToolbarActionButton
                label="Download"
                disabled={!canUseActions || isDownloading}
                onClick={onDownload}
                icon={
                    isDownloading ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                        <ArrowDownTrayIcon className="w-4 h-4" />
                    )
                }
            />
        </div>
    );
}

interface PDFViewerToolbarProps {
    leadingControls?: React.ReactNode;
    page: PageNavData;
    zoom: ZoomData;
    actions: ToolbarActionsData;
}

export function PDFViewerToolbar({ leadingControls, page, zoom, actions }: PDFViewerToolbarProps) {
    return (
        <div className="flex items-center pb-3 border-b border-gray-200 mb-3 gap-2">
            {/* Only shrinkable group, so the filename truncates instead of overrunning the row. */}
            <div className="flex items-center gap-2 flex-1 min-w-0 [&>*]:min-w-0">
                {leadingControls}
            </div>

            {/* Pinned right at content width; the left slot absorbs all the slack. */}
            <div className="flex items-center gap-2 flex-shrink-0">
                <PageNavPill {...page} />
                <ZoomPill {...zoom} />
                <ToolbarActions {...actions} />
            </div>
        </div>
    );
}
